/**
 * Boli Live - Seller Info Card
 *
 * eBay-style seller information with feedback score.
 */

import { Link } from 'react-router-dom';
import { Star, ShieldCheck, MapPin, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';

interface SellerInfoCardProps {
    sellerId: string;
    sellerName: string;
    rating: number;
    feedbackCount: number;
    positivePercentage: number;
    location?: string;
    storeSlug?: string;
    className?: string;
}

export function SellerInfoCard({
    sellerName,
    rating,
    feedbackCount,
    positivePercentage,
    location,
    storeSlug,
    className
}: SellerInfoCardProps) {
    return (
        <div className={cn("bg-card border border-border rounded-lg p-4 space-y-3", className)}>
            <div className="flex items-center justify-between">
                <h3 className="font-bold text-foreground text-sm uppercase tracking-wider text-muted-foreground">Sold By</h3>
            </div>

            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    {/* Avatar / Placeholder */}
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-bold text-lg uppercase border border-border">
                        {sellerName.charAt(0)}
                    </div>

                    <div>
                        <div className="flex items-center gap-1">
                            <span className="font-bold text-foreground hover:underline cursor-pointer">{sellerName}</span>
                            <ShieldCheck className="w-4 h-4 text-blue-500" />
                        </div>

                        <div className="flex items-center gap-1 text-xs mt-0.5">
                            <span className="font-semibold text-foreground">{feedbackCount}</span>
                            <span className="text-muted-foreground">items sold</span>
                            <span className="text-muted-foreground mx-1">•</span>
                            <span className="text-green-600 font-medium">{positivePercentage}% positive</span>
                        </div>
                    </div>
                </div>

                {storeSlug ? (
                    <Link
                        to={`/store/${storeSlug}`}
                        className="text-primary text-sm font-medium hover:underline flex items-center"
                    >
                        Visit Store <ChevronRight className="w-4 h-4" />
                    </Link>
                ) : (
                    <span className="text-primary text-sm font-medium flex items-center opacity-50 cursor-not-allowed">
                        Visit Store <ChevronRight className="w-4 h-4" />
                    </span>
                )}
            </div>

            {/* Rating Bar */}
            <div className="flex items-center gap-2 bg-secondary/50 p-2 rounded-md">
                <div className="flex text-amber-500">
                    {Array.from({ length: 5 }).map((_: unknown, i: number) => (
                        <Star
                            key={i}
                            className={cn("w-4 h-4", i < Math.floor(rating) ? "fill-current" : "text-muted-foreground/30")}
                        />
                    ))}
                </div>
                <span className="text-sm font-medium text-foreground">{rating.toFixed(1)}</span>
            </div>

            {location && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Ships from {location}
                </div>
            )}
        </div>
    );
}
